import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Ion } from "@/components/Ion";
import { getToken } from "@/lib/api";
import { ItemArt } from "@/components/quote/ItemArt";
import { Section, inputCls } from "@/components/quote/QuoteParts";

const BASE = process.env.REACT_APP_BACKEND_URL;
const HINTS = {
  staircase: "e.g. each tread is 36 in wide", kitchen: "e.g. the upper cabinet doors are 15 in wide", interior: "e.g. the door is 32 in wide",
  exterior: "e.g. the front door is 36 in wide", bedroom: "e.g. the dresser is 60 in wide", dining: "e.g. the table is 72 in long",
  living: "e.g. the bookshelf is 36 in wide", fireplace: "e.g. the firebox opening is 36 in wide", floors: "e.g. the room is 12 ft wide",
  automotive: "e.g. 1967 Mustang coupe, the door is 42 in long",
};
const TIPS = {
  staircase: "Start at the bottom, walk up slowly filming the treads, then film the side to show the skirt board, spindles and handrail.",
  kitchen: "Walk the whole room slowly at chest height: uppers, lowers, island and any molding. Open one door to show the box.",
  interior: "Film each door straight on, then a slow pan across both faces and the frame.",
  exterior: "Film the door straight on, then the frame, inside face and any sidelights.",
  bedroom: "Circle each piece slowly so the front, sides and top are visible.", dining: "Circle the table and one chair slowly, then pan across the rest.",
  living: "Circle each piece slowly so the front, sides and top are visible.", fireplace: "Film the full wall, then move close to the mantel, opening and hearth.",
  floors: "Walk the edges of the area so the full outline is in view.", automotive: "Walk all the way around the vehicle slowly, then film under it and each part.",
};
const CONF = { high: "#4CCC06", medium: "#F59E0B", low: "#EF4444" };
const ASK_ICON = { photo: "camera-outline", count: "list-outline", measure: "resize-outline" };

export const shrink = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't read that photo"))), "image/jpeg", 0.85);
  };
  img.onerror = () => reject(new Error("Couldn't open that photo. Try a JPG or PNG."));
  img.src = URL.createObjectURL(file);
});

export const turn = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = img.height; c.height = img.width;
    const g = c.getContext("2d");
    g.translate(c.width, 0); g.rotate(Math.PI / 2); g.drawImage(img, 0, 0);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't turn that photo"))), "image/jpeg", 0.92);
  };
  img.onerror = () => reject(new Error("Couldn't open that photo."));
  img.src = URL.createObjectURL(file);
});

const size = (r, p) => {
  if (p.measure === "floor") return r.sq_ft ? `${r.sq_ft} sq ft` : `${r.width_in} x ${r.height_in} ft`;
  if (p.measure === "linear") return `${r.height_in} ft long, ${r.width_in}" wide`;
  return [r.width_in, r.height_in, ["box", "firebox", "arch"].includes(p.measure) ? r.depth_in : null].filter(Boolean).map((v) => `${v}"`).join(" x ");
};

const poll = async (sid) => {
  for (let i = 0; i < 90; i++) {
    await new Promise((ok) => setTimeout(ok, 2500));
    const r = await fetch(`${BASE}/api/quote-builder/photo-scan/${sid}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const d = await r.json().catch(() => ({}));
    if (d.status === "done") return d.result;
    if (d.status === "error" || !r.ok) throw new Error(d.detail || "Couldn't read the photos");
  }
  throw new Error("The photo read took too long. Try again.");
};

export const EstTag = ({ testId }) => <span className="rounded-full border border-[#F59E0B] px-2 py-0.5 text-[9px] font-extrabold tracking-[1px] text-[#F59E0B]" data-testid={testId}>AI ESTIMATE</span>;

const Proposal = ({ res, byType, picked, setPicked, qtys, setQtys }) => (
  <div className="mt-3" data-testid="photo-scan-results">
    {res.summary && <p className="mb-2 text-[12px] leading-4 text-[#E8E8E8]" data-testid="photo-scan-summary">{res.summary}</p>}
    {res.calibration?.length > 0 && (
      <div className="mb-2 rounded-[10px] border border-[#333] bg-[#0A0A0A] p-2.5" data-testid="photo-scan-calibration">
        <p className="mb-1 text-[10px] font-extrabold tracking-[2px] text-[#4CCC06]">HOW THE REFERENCE WAS USED</p>
        {res.calibration.map((c, i) => <p key={i} className="text-[11px] leading-4 text-[#E8E8E8]"><b style={{ color: c.found ? "#4CCC06" : "#F59E0B" }}>{c.found ? "Found" : "Not found"}:</b> {c.label}. <span className="text-[#A0A0A0]">{c.note}</span></p>)}
        <p className="mt-1 text-[10px] text-[#666]">Looks off? Type the tape-measure number in "Correct the AI" below and rescan.</p>
      </div>
    )}
    {!res.items.length && <p className="text-[12px] text-[#F59E0B]" data-testid="photo-scan-none">Nothing measurable yet. Answer the questions below and rescan.</p>}
    {res.items.map((r, i) => (
      <div key={i} className="mb-1.5 flex items-center gap-3 rounded-[10px] border border-[#333] bg-[#121212] p-2.5" data-testid={`photo-scan-row-${i}`}>
        <input type="checkbox" checked={picked.has(i)} onChange={() => setPicked((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; })} className="accent-[#4CCC06]" data-testid={`photo-scan-check-${i}`} />
        <ItemArt type={r.type} className="h-10 w-10 text-[#4CCC06]" />
        <input inputMode="numeric" value={qtys[i] ?? r.qty} onChange={(e) => setQtys((q) => ({ ...q, [i]: e.target.value }))} className="w-12 rounded-[8px] border border-[#333] bg-[#0A0A0A] px-1.5 py-1 text-center text-[13px] font-bold text-white focus:border-[#4CCC06] focus:outline-none" data-testid={`photo-scan-qty-${i}`} aria-label="Count" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold">× {byType[r.type].label} <span className="font-normal text-[#A0A0A0]">· {size(r, byType[r.type])}</span></p>
          {r.why && <p className="truncate text-[11px] text-[#A0A0A0]">{r.why}</p>}
        </div>
        {r.estimated && <EstTag testId={`photo-scan-est-${i}`} />}
        <span className="rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase" style={{ color: CONF[r.confidence], border: `1px solid ${CONF[r.confidence]}` }}>{r.confidence}</span>
      </div>
    ))}
    {res.warnings.map((w, i) => <p key={i} className="mt-1 text-[11px] text-[#F59E0B]">• {w}</p>)}
  </div>
);

const Missing = ({ items, answers, setAnswers, onPhoto }) => (
  <div className="mt-3 rounded-[12px] border border-[#F59E0B] bg-[#F59E0B0D] p-3" data-testid="photo-scan-missing">
    <p className="mb-2 text-[10px] font-extrabold tracking-[2px] text-[#F59E0B]">THE AI NEEDS A LITTLE MORE</p>
    {items.map((m) => {
      const a = answers[m.key] || {};
      return (
        <div key={m.key} className="mb-3 last:mb-0" data-testid={`missing-${m.key}`}>
          <p className="mb-1.5 flex items-start gap-2 text-[12px] leading-4"><Ion name={ASK_ICON[m.ask]} size={14} color="#F59E0B" />{m.question}</p>
          <div className="flex flex-wrap gap-2">
            {!a.guess && <input className={`${inputCls} min-w-[160px] flex-1 !py-2`} value={a.text || ""} placeholder={m.ask === "count" ? "How many?" : m.ask === "photo" ? "Or describe it" : "Size, e.g. 36 x 11 in"} onChange={(e) => setAnswers((s) => ({ ...s, [m.key]: { ...a, text: e.target.value } }))} data-testid={`missing-input-${m.key}`} />}
            {m.ask === "photo" && <button onClick={onPhoto} className="rounded-full border border-[#555] px-3 py-1.5 text-[11px] font-bold text-white" data-testid={`missing-photo-${m.key}`}>+ Add photo</button>}
            <button onClick={() => setAnswers((s) => ({ ...s, [m.key]: { ...a, guess: !a.guess } }))} data-testid={`missing-guess-${m.key}`}
              className={`rounded-full border px-3 py-1.5 text-[11px] font-extrabold ${a.guess ? "border-[#F59E0B] bg-[#F59E0B] text-black" : "border-[#F59E0B] text-[#F59E0B]"}`}>{a.guess ? "✓ AI will estimate" : "Figure it out for me"}</button>
          </div>
        </div>
      );
    })}
  </div>
);

const uploadVideo = (file, onPct) => new Promise((resolve, reject) => {
  const x = new XMLHttpRequest();
  x.open("POST", `${BASE}/api/quote-builder/scan-video`);
  x.setRequestHeader("Authorization", `Bearer ${getToken()}`);
  x.upload.onprogress = (e) => e.lengthComputable && onPct(Math.round((e.loaded / e.total) * 100));
  x.onload = () => { let j = {}; try { j = JSON.parse(x.responseText); } catch {} x.status < 300 ? resolve(j) : reject(new Error(typeof j.detail === "string" ? j.detail : "Video upload failed")); };
  x.onerror = () => reject(new Error("Video upload failed. Check your connection."));
  const fd = new FormData(); fd.append("file", file); x.send(fd);
});

const videoSeconds = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata"; v.onloadedmetadata = () => resolve(v.duration); v.onerror = () => resolve(0);
  v.src = URL.createObjectURL(file);
});

export const PhotoScan = ({ builder, label, byType, onAdd, refs = [], onVideo }) => {
  const [vPct, setVPct] = useState(null);
  const [tips, setTips] = useState(false);
  const vref = useRef(null);
  const pickVideo = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const s = await videoSeconds(f);
    if (s > 61) return toast.error("Keep the video under 60 seconds");
    setVPct(0); setTips(false);
    try {
      const j = await uploadVideo(f, setVPct);
      setStored((x) => [...x, ...j.frames]); onVideo?.(j.video_id);
      toast.success(`Video added: ${j.frames.length} frames ready. Enter a measurement and tap Count & Measure.`);
    } catch (err) { toast.error(err.message); } finally { setVPct(null); }
  };
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [stored, setStored] = useState([]);
  const [known, setKnown] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [qtys, setQtys] = useState({});
  const [answers, setAnswers] = useState({});
  const [history, setHistory] = useState([]);
  const [fix, setFix] = useState("");
  const [added, setAdded] = useState([]);
  const [ack, setAck] = useState(false);
  useEffect(() => setAck(false), [res]);
  const ref = useRef(null);
  const reset = () => { setRes(null); setFiles([]); setStored([]); setHistory([]); setAnswers({}); setFix(""); setAdded([]); setQtys({}); };
  useEffect(reset, [builder]);

  const pick = (e) => { const fs = [...(e.target.files || [])]; e.target.value = ""; setFiles((x) => [...x, ...fs].slice(0, 3)); };
  const scan = async () => {
    if (!files.length && !stored.length) return toast.error("Add at least one photo");
    if (known.trim().length < 3) return toast.error("Add one measurement you know so sizes are accurate");
    const newAnswers = (res?.missing || []).filter((m) => answers[m.key]?.guess || answers[m.key]?.text?.trim())
      .map((m) => ({ question: m.question, answer: answers[m.key].guess ? "__guess__" : answers[m.key].text.trim() }));
    const qa = [...history, ...newAnswers];
    const counts = (res?.items || []).filter((r, i) => qtys[i] !== undefined && String(qtys[i]) !== String(r.qty)).map((r) => `It is exactly ${qtys[res.items.indexOf(r)]} x ${byType[r.type].label}.`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("builder", builder); fd.append("known", known.trim()); fd.append("notes", notes.trim());
      fd.append("photo_ids", stored.join(",")); fd.append("answers", JSON.stringify(qa)); fd.append("corrections", [fix.trim(), ...counts].filter(Boolean).join(" "));
      fd.append("refs", JSON.stringify(refs.filter((r) => r.label.trim()).map((r) => ({ photo_id: r.id, label: r.label.trim() }))));
      for (const f of files) fd.append("files", await shrink(f), "photo.jpg");
      const r = await fetch(`${BASE}/api/quote-builder/photo-scan`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const st = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof st.detail === "string" ? st.detail : "Couldn't read the photos");
      let j = await poll(st.scan_id);
      if (!j.items.length && res?.items.length) { j = { ...j, items: res.items }; toast.warning("The AI didn't return an updated list, so your previous list is kept. Edit counts above or rescan."); }
      setRes(j); setStored(j.photos); setFiles([]); setHistory(qa); setAnswers({}); setQtys({}); setFix("");
      setPicked(new Set(j.items.map((_, i) => i)));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const apply = () => {
    const rows = res.items.map((r, i) => ({ ...r, qty: parseInt(qtys[i] ?? r.qty, 10) || r.qty })).filter((_, i) => picked.has(i));
    setAdded(onAdd(rows, stored, added));
    toast.success(`${added.length ? "Updated" : "Added"} ${rows.length} item${rows.length === 1 ? "" : "s"}. Check the sizes below.`);
    if (!res.missing.length) { reset(); setOpen(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="photo-scan-open" className="mb-3 flex w-full items-center justify-center gap-2 rounded-[12px] border border-dashed border-[#4CCC06] bg-[#4CCC060D] py-3 text-xs font-extrabold tracking-[1px] text-[#4CCC06] transition-colors hover:bg-[#4CCC061A]">
      <Ion name="camera-outline" size={16} color="#4CCC06" />QUOTE {label.toUpperCase()} FROM A PHOTO OR VIDEO
      <span className="rounded-full bg-[#F59E0B] px-2 py-0.5 text-[9px] font-extrabold tracking-[1px] text-black" data-testid="photo-scan-beta-tag">NEW · BETA</span>
    </button>
  );
  return (
    <div className="pb-14 sm:pb-0"><Section title={`PHOTO TO QUOTE · ${label.toUpperCase()}`} testId="photo-scan">
      <div className="mb-3 flex items-start gap-2 rounded-[10px] border border-[#F59E0B] bg-[#F59E0B12] p-2.5" data-testid="photo-scan-beta-note">
        <Ion name="alert-circle" size={16} color="#F59E0B" />
        <p className="flex-1 text-[12px] leading-4"><b className="text-[#F59E0B]">NEW FEATURE · BETA.</b> AI measuring can miss parts or get sizes wrong. Use it to save time, then double-check every count and size before you send a price.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {stored.map((id) => <Thumb key={id} id={id} small />)}
        {files.map((f, i) => (
          <div key={i} className="relative h-20 w-20 overflow-hidden rounded-[10px] border border-[#4CCC06]">
            <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
            <button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5" data-testid={`photo-scan-remove-${i}`} aria-label="Remove"><Ion name="close" size={12} color="#fff" /></button>
            <button onClick={() => turn(f).then((b) => setFiles((x) => x.map((y, j) => (j === i ? b : y)))).catch((e) => toast.error(e.message))} className="absolute bottom-1 left-1 rounded-full bg-black/70 p-1" data-testid={`photo-scan-rotate-${i}`} aria-label="Turn photo upright" title="Sideways? Tap to turn"><Ion name="refresh-outline" size={13} color="#fff" /></button>
          </div>
        ))}
        {files.length < 3 && stored.length + files.length < 16 && <button onClick={() => ref.current?.click()} data-testid="photo-scan-add" className="flex h-20 w-20 flex-col items-center justify-center rounded-[10px] border border-dashed border-[#555] text-[10px] font-bold text-[#A0A0A0] hover:border-[#4CCC06]"><Ion name="camera-outline" size={20} color="#A0A0A0" />Add photo</button>}
        <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid="photo-scan-input" />
        {stored.length <= 4 && <button onClick={() => setTips((t) => !t)} disabled={vPct !== null} data-testid="video-scan-add" className="flex h-20 w-20 flex-col items-center justify-center rounded-[10px] border border-dashed border-[#555] text-[10px] font-bold text-[#A0A0A0] hover:border-[#4CCC06] disabled:opacity-60">{vPct !== null ? <><Loader2 size={18} className="animate-spin" />{vPct < 100 ? `${vPct}%` : "Reading"}</> : <><Ion name="videocam-outline" size={20} color="#A0A0A0" />Use a video</>}</button>}
        <input ref={vref} type="file" accept="video/*" className="hidden" onChange={pickVideo} data-testid="video-scan-input" />
      </div>
      {files.length > 0 && <p className="mt-1.5 text-[11px] text-[#A0A0A0]" data-testid="photo-scan-rotate-hint">Photo sideways? Tap the turn button on the photo until it's upright. Sideways photos measure badly.</p>}
      {tips && (
        <div className="mt-2.5 rounded-[10px] border border-[#4CCC06] bg-[#4CCC060D] p-3" data-testid="video-tips">
          <p className="text-[10px] font-extrabold tracking-[2px] text-[#4CCC06]">FILMING TIPS · UP TO 60 SECONDS</p>
          <p className="mt-1 text-[12px] leading-4">{TIPS[builder]}</p>
          <p className="mt-1 text-[11px] text-[#A0A0A0]">Move slowly, keep good light, and film the item you measured.</p>
          <button onClick={() => vref.current?.click()} data-testid="video-scan-pick" className="mt-2 rounded-full bg-[#4CCC06] px-4 py-2 text-[11px] font-extrabold text-black">RECORD OR CHOOSE VIDEO</button>
        </div>
      )}
      <p className="mb-1.5 mt-3 text-[11px] font-semibold text-[#A0A0A0]">One measurement you know (used as the scale)</p>
      <input className={inputCls} value={known} onChange={(e) => setKnown(e.target.value)} placeholder={HINTS[builder]} data-testid="photo-scan-known" />
      <p className="mb-1.5 mt-2.5 text-[11px] font-semibold text-[#A0A0A0]">Anything the photo doesn't show? (optional)</p>
      <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. hidden parts, other side not shown, more pieces in another room" data-testid="photo-scan-notes" />
      {res && <Proposal res={res} byType={byType} picked={picked} setPicked={setPicked} qtys={qtys} setQtys={setQtys} />}
      {res?.items.length > 0 && (
        <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-[10px] border border-[#F59E0B] bg-[#F59E0B12] p-3" data-testid="photo-scan-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#4CCC06]" data-testid="photo-scan-ack-box" />
          <span className="text-[12px] leading-4">I'll check every count and size against the job before I send this price. AI measuring is new and can be wrong.</span>
        </label>
      )}
      {res?.items.length > 0 && <button onClick={apply} disabled={!picked.size || !ack} data-testid="photo-scan-apply" className="mt-2 w-full rounded-[10px] border border-[#4CCC06] py-3 text-xs font-extrabold tracking-[1px] text-[#4CCC06] disabled:opacity-40">{added.length ? "REPLACE" : "ADD"} {picked.size} ITEM{picked.size === 1 ? "" : "S"} {added.length ? "IN" : "TO"} THE QUOTE</button>}
      {res?.missing.length > 0 && <Missing items={res.missing} answers={answers} setAnswers={setAnswers} onPhoto={() => ref.current?.click()} />}
      {res && (
        <><p className="mb-1.5 mt-3 text-[11px] font-semibold text-[#A0A0A0]">Correct the AI (optional), or change a count in the list above</p>
          <input className={inputCls} value={fix} onChange={(e) => setFix(e.target.value)} placeholder="e.g. it's 14 treads, the landing is 42 x 42 in" data-testid="photo-scan-fix" /></>
      )}
      <div className="mt-3 flex gap-2">
        <button onClick={scan} disabled={busy} data-testid="photo-scan-run" className="flex flex-1 items-center justify-center gap-2 rounded-[10px] bg-[#4CCC06] py-3 text-xs font-extrabold tracking-[1px] text-black disabled:opacity-60">{busy ? <><Loader2 size={14} className="animate-spin" />READING PHOTOS (1–3 MINUTES)</> : res ? "RESCAN WITH MY ANSWERS" : "COUNT & MEASURE"}</button>
        <button onClick={() => { reset(); setOpen(false); }} data-testid="photo-scan-close" className="rounded-[10px] border border-[#333] px-4 text-xs font-bold">Close</button>
      </div>
    </Section></div>
  );
};

export const Thumb = ({ id, small }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    fetch(`${BASE}/api/quote-builder/photos/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.blob() : null)).then((b) => { if (b) { url = URL.createObjectURL(b); setSrc(url); } }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  const cls = small ? "h-20 w-20 rounded-[10px]" : "h-16 w-16 rounded-[8px]";
  return src ? <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="Job" className={`${cls} border border-[#333] object-cover`} data-testid={`job-photo-${id}`} /></a> : <div className={`${cls} animate-pulse bg-[#222]`} />;
};

export const playVideo = async (id) => {
  try {
    const r = await fetch(`${BASE}/api/quote-builder/photos/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) throw new Error();
    window.open(URL.createObjectURL(await r.blob()), "_blank");
  } catch { toast.error("Couldn't open the video"); }
};

export const JobPhotos = ({ ids, videos = [] }) => !ids.length && !videos.length ? null : (
  <div className="mb-3 rounded-[12px] border border-[#333] bg-[#1A1A1A] p-3" data-testid="job-photos">
    <p className="mb-2 text-[10px] font-extrabold tracking-[2px] text-[#4CCC06]">JOB PHOTOS · SAVED WITH THIS QUOTE</p>
    <div className="flex flex-wrap items-center gap-2">
      {videos.map((v, i) => <button key={v} onClick={() => playVideo(v)} data-testid={`job-video-${i}`} className="flex h-16 items-center gap-1.5 rounded-[8px] border border-[#4CCC06] px-3 text-[11px] font-extrabold text-[#4CCC06]"><Ion name="play-circle-outline" size={18} color="#4CCC06" />Video {i + 1}</button>)}
      {ids.map((id) => <Thumb key={id} id={id} />)}
    </div>
  </div>
);

export const AiCheckNote = ({ n, testId }) => (
  <div className="mb-3 flex items-start gap-2 rounded-[12px] border border-[#F59E0B] bg-[#F59E0B12] p-3" data-testid={testId}>
    <Ion name="alert-circle" size={16} color="#F59E0B" />
    <p className="flex-1 text-[12px] leading-4"><b className="text-[#F59E0B]">{n} AI-measured part{n === 1 ? " needs" : "s need"} checking (new feature, beta).</b> Check each count and size against the job, then tap its AI MEASURED tag. Do this before you send the price to the customer.</p>
  </div>
);
